import { useState, type KeyboardEvent } from 'react';
import { TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useUpdateEntry } from '../../../queries/runsheet-entries';
import { ApiError } from '../../../lib/api-error';
import type { RunsheetEntry } from '../../../types/api';

type Field = 'book' | 'page' | 'instrument_number';

interface Props {
  entry: RunsheetEntry;
  field: Field;
  placeholder?: string | undefined;
  width?: number | undefined;
}

export function InlineCellText({ entry, field, placeholder, width }: Props) {
  const serverValue = entry[field] ?? '';
  const [value, setValue] = useState(serverValue);
  const [lastServer, setLastServer] = useState(serverValue);
  const [saving, setSaving] = useState(false);
  const updateM = useUpdateEntry();

  // React-docs-approved "adjust state based on prop change" during render.
  if (lastServer !== serverValue) {
    setLastServer(serverValue);
    setValue(serverValue);
  }

  async function commit() {
    const next = value.trim();
    const current = entry[field] ?? '';
    if (next === current) return;
    try {
      setSaving(true);
      await updateM.mutateAsync({ id: entry.id, patch: { [field]: next || null } });
    } catch (err) {
      setValue(current);
      notifications.show({
        color: 'red',
        title: 'Update failed',
        message: err instanceof ApiError ? err.message : 'Retry?',
      });
    } finally {
      setSaving(false);
    }
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    } else if (e.key === 'Escape') {
      setValue(entry[field] ?? '');
      (e.target as HTMLInputElement).blur();
    }
  }

  return (
    <span data-inline-editor onClick={(e) => e.stopPropagation()}>
      <TextInput
        size="xs"
        variant="unstyled"
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.currentTarget.value)}
        onBlur={() => void commit()}
        onKeyDown={onKey}
        className="rt-inline-edit-cell"
        w={width}
        disabled={saving}
        styles={{ input: { minHeight: 28, padding: '2px 6px' } }}
      />
    </span>
  );
}
